import { supabase } from './supabaseClient';
import { logger } from '@shared/lib/logger';
import { recordSlowApi } from '@shared/lib/slow-api-tracker';
import { isDoctorControlPath } from '@shared/lib/doctor-workspace';
import { clearDoctorControlStorage, getDoctorControlRequestIdFromStorage } from '@shared/stores/doctor-control-store';

export interface ApiResponse<T> {
  data: T;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  meta?: {
    timestamp: string;
    version: string;
    requestId: string;
  };
}

export interface CacheConfig {
  ttl?: number; // Time to live in milliseconds
  staleWhileRevalidate?: boolean;
  tags?: string[]; // Cache tags for invalidation
}

export interface RequestConfig extends Omit<RequestInit, 'cache' | 'priority'> {
  cache?: CacheConfig;
  timeout?: number;
  retries?: number;
  priority?: 'low' | 'normal' | 'high';
  responseType?: 'json' | 'blob' | 'text';
  useFreshSessionAuth?: boolean;
  timeoutProfile?: 'default' | 'messaging' | 'quotingQueue' | 'travelPlanning' | 'dashboard' | 'background';
  dedupe?: 'auto' | 'off';
  requestLabel?: string;
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, any>;
    stack?: string;
  };
}

export type ApiErrorKind =
  | 'timeout'
  | 'network'
  | 'rate_limit'
  | 'abort'
  | 'auth'
  | 'server'
  | 'client'
  | 'unknown';

export class ApiClientError extends Error {
  status: number;
  code?: string;
  details?: Record<string, any>;
  kind: ApiErrorKind;
  retryable: boolean;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: Record<string, any>,
    kind?: ApiErrorKind,
    retryable?: boolean,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.kind = kind ?? classifyApiErrorFromStatus(status, code);
    this.retryable = retryable ?? isRetryableErrorKind(this.kind);
  }
}

const isRetryableErrorKind = (kind: ApiErrorKind): boolean =>
  kind === 'timeout' || kind === 'network' || kind === 'rate_limit' || kind === 'server';

const classifyApiErrorFromStatus = (status: number, code?: string): ApiErrorKind => {
  if (code === 'REQUEST_ABORTED') return 'abort';
  if (code === 'REQUEST_TIMEOUT') return 'timeout';
  if (status === 401 || status === 403) return 'auth';
  if (status === 408) return 'timeout';
  if (status === 429) return 'rate_limit';
  if (status >= 500) return 'server';
  if (status >= 400) return 'client';
  if (status === 0) return 'network';
  return 'unknown';
};

/**
 * RFC 7231 §7.1.3 — Retry-After is either delta-seconds (1*DIGIT) or an
 * HTTP-date. Exported for direct unit testing.
 */
export const parseRetryAfterMs = (headerValue: string | null, fallbackMs: number): number => {
  if (!headerValue) return fallbackMs;
  const trimmed = headerValue.trim();
  if (/^\d+$/.test(trimmed)) {
    return parseInt(trimmed, 10) * 1000;
  }
  const ts = Date.parse(trimmed);
  if (!isNaN(ts)) {
    return Math.max(0, ts - Date.now());
  }
  return fallbackMs;
};

export const classifyApiError = (error: unknown): ApiErrorKind => {
  if (error instanceof ApiClientError) return error.kind;
  if (error instanceof DOMException && error.name === 'AbortError') return 'abort';
  if (error instanceof DOMException && error.name === 'TimeoutError') return 'timeout';
  if (error instanceof TypeError) return 'network';
  return 'unknown';
};

class ApiClient {
  private static readonly ACTIVE_USER_STORAGE_KEY = 'jema_active_user_id_v2';
  private baseUrl: string;
  private tokenRefreshPromise: Promise<void> | null = null;
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private requestCache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
  private enableCustomHeaders = false;
  private rateLimitState = {
    requests: 0,
    resetTime: Date.now() + 60000, // Reset every minute
    maxRequests: 1000, // Increased from 100 to 1000 requests per minute
  };
  private readonly timeoutProfiles: Record<NonNullable<RequestConfig['timeoutProfile']>, number> = {
    default: 15_000,
    messaging: 10_000,
    quotingQueue: 12_000,
    travelPlanning: 20_000,
    dashboard: 8_000,
    background: 30_000,
  };

  constructor() {
    const rawUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    const isBrowser = typeof window !== 'undefined';
    const hostname = isBrowser ? window.location.hostname : '';
    const isLocalhostHost = hostname === 'localhost' || hostname === '127.0.0.1';

    // Normalize base URL to always target /api/v2 without duplicating versioned segments.
    const trimTrailingSlash = (value: string) => value.replace(/\/+$/g, '');
    const urlNoTrail = trimTrailingSlash(rawUrl);

    let normalizedBase = urlNoTrail;
    const hasApi = /\/(api)(\/|$)/.test(urlNoTrail);
    const endsWithV2 = /\/v2$/.test(urlNoTrail);
    const apiSegmentRegex = /\/api(?:\/[^?#]*)*(?:[?#].*)?$/;

    if (endsWithV2) {
      normalizedBase = urlNoTrail;
    } else if (hasApi && apiSegmentRegex.test(urlNoTrail)) {
      normalizedBase = urlNoTrail.replace(apiSegmentRegex, "/api/v2");
    } else {
      normalizedBase = urlNoTrail + '/api/v2';
    }

    // In local browser development, prefer same-origin API calls and let Vite proxy
    // `/api/*` to the local gateway. This avoids stale absolute env hosts silently
    // sending requests to a different machine than the one the developer is watching.
    this.baseUrl = isLocalhostHost ? '/api/v2' : normalizedBase;
    
    console.log('API Client initialized with base URL:', this.baseUrl);
    console.log('Environment VITE_API_URL:', import.meta.env.VITE_API_URL);
    
    // Enable custom headers only if explicitly configured
    this.enableCustomHeaders = import.meta.env.VITE_ENABLE_CUSTOM_HEADERS === 'true';

    // Start cache cleanup interval
    this.startCacheCleanup();
  }

  private async getAuthToken(): Promise<string | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }

  private getExplicitAuthorizationHeader(headers?: HeadersInit): string | null {
    if (!headers) return null;

    if (headers instanceof Headers) {
      return headers.get('Authorization');
    }

    if (Array.isArray(headers)) {
      const match = headers.find(([key]) => key.toLowerCase() === 'authorization');
      return match?.[1] ?? null;
    }

    for (const [key, value] of Object.entries(headers)) {
      if (key.toLowerCase() === 'authorization') {
        return typeof value === 'string' ? value : null;
      }
    }

    return null;
  }

  private isInvalidRefreshTokenError(error: unknown): boolean {
    const message = String((error as any)?.message ?? '').toLowerCase();
    const code = String((error as any)?.code ?? '').toLowerCase();
    return (
      message.includes('invalid refresh token') ||
      message.includes('refresh token not found') ||
      message.includes('jwt expired') ||
      code.includes('refresh_token')
    );
  }

  private async clearInvalidSession(reason: string, error?: unknown): Promise<void> {
    logger.warn('Clearing invalid local auth session', {
      reason,
      error: (error as any)?.message ?? String(error ?? ''),
    });

    try {
      await supabase.auth.signOut({ scope: 'local' } as any);
    } catch (signOutError) {
      logger.debug('Local sign out failed while clearing invalid session', {
        error: (signOutError as any)?.message ?? String(signOutError),
      });
    }

    try {
      localStorage.removeItem('jema_auth_store_v2');
      localStorage.removeItem('jema_auth_state');
      localStorage.removeItem('userId');
    } catch {
      // no-op
    }
  }

  private async refreshTokenIfNeeded(): Promise<void> {
    // If a refresh is already in progress, wait for it
    if (this.tokenRefreshPromise) {
      await this.tokenRefreshPromise;
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    
    if (session && session.expires_at) {
      const expiresAt = session.expires_at * 1000; // Convert to milliseconds
      const now = Date.now();
      const tenMinutes = 10 * 60 * 1000; // Increased buffer to 10 minutes
      
      // Refresh if token expires in less than 10 minutes
      if (expiresAt - now < tenMinutes) {
        logger.debug('Token refresh needed, refreshing');
        
        this.tokenRefreshPromise = supabase.auth
          .refreshSession()
          .then(async ({ error }) => {
            if (!error) {
              logger.debug('Token refreshed successfully');
              return;
            }

            if (this.isInvalidRefreshTokenError(error)) {
              await this.clearInvalidSession('proactive_refresh_failed', error);
              throw new ApiClientError(
                'Authentication required. Please sign in again.',
                401,
                'AUTHENTICATION_REQUIRED',
                undefined,
                'auth',
                false
              );
            }

            logger.error('Token refresh failed', error, { stage: 'refreshTokenIfNeeded' });
            throw error;
          })
          .finally(() => {
            this.tokenRefreshPromise = null;
          });
        
        await this.tokenRefreshPromise;
      }
    }
  }

  private startCacheCleanup(): void {
    setInterval(() => {
      const now = Date.now();
      for (const [key, entry] of this.requestCache.entries()) {
        if (now > entry.timestamp + entry.ttl) {
          this.requestCache.delete(key);
        }
      }
    }, 60000); // Clean up every minute
  }

  private checkRateLimit(): boolean {
    const now = Date.now();
    
    // Reset rate limit if time window has passed
    if (now > this.rateLimitState.resetTime) {
      this.rateLimitState.requests = 0;
      this.rateLimitState.resetTime = now + 60000;
    }
    
    return this.rateLimitState.requests < this.rateLimitState.maxRequests;
  }

  private getRequestScopeKey(): string {
    if (typeof window === 'undefined') {
      return 'scope:ssr';
    }

    try {
      const activeUserId = window.localStorage.getItem(ApiClient.ACTIVE_USER_STORAGE_KEY);
      if (activeUserId) {
        return `scope:user:${activeUserId}`;
      }
    } catch (error) {
      logger.warn('Failed to read active user id for request scoping', { error });
    }

    return 'scope:anon';
  }

  private getCacheKey(endpoint: string, options: Pick<RequestConfig, 'method' | 'body'>, scopeKey: string): string {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    return `${scopeKey}:${method}:${endpoint}:${body}`;
  }

  private resolveTimeout(profile: RequestConfig['timeoutProfile'], endpoint: string): number {
    if (profile) return this.timeoutProfiles[profile];
    if (endpoint.includes('/messages')) return this.timeoutProfiles.messaging;
    if (endpoint.includes('/quoting-queue')) return this.timeoutProfiles.quotingQueue;
    if (endpoint.includes('/travel-planning')) return this.timeoutProfiles.travelPlanning;
    if (endpoint.includes('/dashboard')) return this.timeoutProfiles.dashboard;
    return this.timeoutProfiles.default;
  }

  private normalizeEndpointForDedup(endpoint: string): string {
    const [path, queryString = ''] = endpoint.split('?');
    if (!queryString) return path;
    const params = new URLSearchParams(queryString);
    const sorted = Array.from(params.entries()).sort(([a], [b]) => a.localeCompare(b));
    const normalizedParams = new URLSearchParams();
    sorted.forEach(([key, value]) => normalizedParams.append(key, value));
    const normalizedQuery = normalizedParams.toString();
    return normalizedQuery ? `${path}?${normalizedQuery}` : path;
  }

  private createRequestSignal(timeoutMs: number, externalSignal?: AbortSignal | null) {
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort(new DOMException('Request timed out', 'TimeoutError'));
    }, timeoutMs);

    const handleExternalAbort = () => {
      controller.abort((externalSignal as AbortSignal).reason);
    };

    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort(externalSignal.reason);
      } else {
        externalSignal.addEventListener('abort', handleExternalAbort, { once: true });
      }
    }

    return {
      signal: controller.signal,
      didTimeout: () => timedOut,
      cleanup: () => {
        clearTimeout(timeoutId);
        if (externalSignal) {
          externalSignal.removeEventListener('abort', handleExternalAbort);
        }
      },
    };
  }

  private getFromCache(cacheKey: string): any | null {
    const entry = this.requestCache.get(cacheKey);
    if (!entry) return null;
    
    const now = Date.now();
    if (now > entry.timestamp + entry.ttl) {
      this.requestCache.delete(cacheKey);
      return null;
    }
    
    return entry.data;
  }

  private setCache(cacheKey: string, data: any, ttl: number): void {
    this.requestCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  public invalidateCache(pattern?: string): void {
    if (!pattern) {
      this.requestCache.clear();
      return;
    }
    
    for (const key of this.requestCache.keys()) {
      if (key.includes(pattern)) {
        this.requestCache.delete(key);
      }
    }
  }

  public clearSessionBoundState(reason: string): void {
    const requestCacheSize = this.requestCache.size;
    const pendingRequestCount = this.pendingRequests.size;

    this.requestCache.clear();
    this.pendingRequests.clear();

    logger.info('Cleared API client session-bound state', {
      reason,
      requestCacheSize,
      pendingRequestCount,
    });
  }

  /**
   * Configure whether to send custom headers (X-Request-ID, X-Client-Version)
   * Disable this if you encounter CORS issues
   */
  public setCustomHeadersEnabled(enabled: boolean): void {
    this.enableCustomHeaders = enabled;
  }

  /**
   * Get current custom headers configuration
   */
  public isCustomHeadersEnabled(): boolean {
    return this.enableCustomHeaders;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestConfig = {},
    retryCount = 0
  ): Promise<T> {
    const {
      cache,
      timeout,
      retries: _retries = 3,
      priority: _priority = 'normal',
      timeoutProfile,
      dedupe = 'auto',
      requestLabel,
      ...requestOptions
    } = options;
    const resolvedTimeout = timeout ?? this.resolveTimeout(timeoutProfile, endpoint);
    const scopeKey = this.getRequestScopeKey();
    
    // Create cache key
    const cacheKey = this.getCacheKey(endpoint, requestOptions, scopeKey);
    
    // Check cache first for GET requests
    if (requestOptions.method === 'GET' || !requestOptions.method) {
      const cachedData = this.getFromCache(cacheKey);
      if (cachedData) {
        logger.debug('Returning cached data', { endpoint, cacheKey });
        return cachedData;
      }
    }
    
    const method = (requestOptions.method || 'GET').toUpperCase();
    const shouldDedupe = method === 'GET' && dedupe !== 'off';
    const requestKey = shouldDedupe
      ? `${scopeKey}:${method}:${this.normalizeEndpointForDedup(endpoint)}`
      : '';

    if (shouldDedupe) {
      const pendingRequest = this.pendingRequests.get(requestKey);
      if (pendingRequest) {
        logger.debug('Deduplicating concurrent request', { requestKey, requestLabel });
        return pendingRequest;
      }
    }

    const requestPromise = this.executeRequest<T>(
      endpoint,
      requestOptions,
      retryCount,
      resolvedTimeout,
      requestLabel
    );

    if (shouldDedupe) {
      this.pendingRequests.set(requestKey, requestPromise);
    }
    
    try {
      const result = await requestPromise;
      
      // Cache GET requests if cache config is provided
      if ((requestOptions.method === 'GET' || !requestOptions.method) && cache?.ttl) {
        this.setCache(cacheKey, result, cache.ttl);
      }
      
      return result;
    } finally {
      // Clean up the pending request
      if (shouldDedupe) {
        this.pendingRequests.delete(requestKey);
      }
    }
  }

  private async executeRequest<T>(
    endpoint: string,
    options: RequestConfig = {},
    retryCount = 0,
    timeout = 30000,
    requestLabel?: string,
  ): Promise<T> {
    const startTime = Date.now();
    const requestSignal = this.createRequestSignal(timeout, options.signal ?? null);
    const method = options.method || 'GET';
    
    try {
      console.log('[api-client] execute start', {
        endpoint,
        requestLabel,
        method,
        abortedAtStart: requestSignal.signal.aborted,
      });

      // Only check and charge quota on the first attempt; retries are part of
      // the same logical request and must not consume additional quota slots.
      if (retryCount === 0) {
        if (!this.checkRateLimit()) {
          throw new ApiClientError(
            'Rate limit exceeded. Please try again later.',
            429,
            'RATE_LIMIT_EXCEEDED',
            undefined,
            'rate_limit',
            true
          );
        }
        this.rateLimitState.requests++;
      }
      
      const explicitAuthorizationHeader = this.getExplicitAuthorizationHeader(options.headers);
      const shouldUseFreshSessionAuth = options.useFreshSessionAuth === true;

      // Ensure the ambient session token is fresh before we use it. Requests
      // that provide their own Authorization header can opt into using the
      // latest refreshed session token instead.
      if (!explicitAuthorizationHeader || shouldUseFreshSessionAuth) {
        console.log('[api-client] refreshing session if needed', {
          endpoint,
          requestLabel,
        });
        await this.refreshTokenIfNeeded();
      }

      const token = explicitAuthorizationHeader && !shouldUseFreshSessionAuth
        ? null
        : await this.getAuthToken();
      
      const url = /^https?:\/\//.test(endpoint)
        ? endpoint
        : endpoint.startsWith('/api/')
          ? `${this.baseUrl.replace(/\/api\/v2$/, '')}${endpoint}`
          : `${this.baseUrl}${endpoint}`;
      console.log('[api-client] fetch start', {
        endpoint,
        requestLabel,
        method,
        url,
        hasToken: Boolean(token),
        abortedBeforeFetch: requestSignal.signal.aborted,
      });
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      // Only add custom headers if explicitly enabled (to avoid CORS issues)
      if (this.enableCustomHeaders) {
        headers['X-Request-ID'] = crypto.randomUUID();
        headers['X-Client-Version'] = '1.0.0';
      }

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const doctorControlRequestId = getDoctorControlRequestIdFromStorage();
      const shouldAttachDoctorControlHeader =
        typeof window !== 'undefined' && isDoctorControlPath(window.location.pathname);
      if (doctorControlRequestId && shouldAttachDoctorControlHeader) {
        headers['x-doctor-control-request-id'] = doctorControlRequestId;
      }

      const {
        cache: _cache,
        timeout: _timeout,
        retries: _retries,
        priority: _priority,
        responseType: _responseType,
        useFreshSessionAuth: _useFreshSessionAuth,
        timeoutProfile: _timeoutProfile,
        dedupe: _dedupe,
        requestLabel: _requestLabel,
        ...fetchOptions
      } = options;

      const config: RequestInit = {
        ...fetchOptions,
        headers,
        signal: requestSignal.signal,
      };

      const response = await fetch(url, config);
      console.log('[api-client] fetch response', {
        endpoint,
        requestLabel,
        method,
        url,
        status: response.status,
        ok: response.ok,
      });
      if (!response || typeof response.ok !== 'boolean') {
        throw new TypeError('Invalid fetch response');
      }
      
      // Log performance metrics
      const duration = Date.now() - startTime;
      logger.performanceMetric('api_request_duration', duration, 'ms');
      recordSlowApi(endpoint, options.method || 'GET', duration);

      if (!response.ok) {
        return await this.handleErrorResponse<T>(response, endpoint, options, retryCount);
      }

      // Handle empty responses (like 204 No Content)
      if (response.status === 204) {
        return {} as T;
      }

      const contentType = response.headers?.get?.('content-type') || '';
      const requestedResponseType = options.responseType;
      const canReadBlob = typeof (response as Response & { blob?: () => Promise<Blob> }).blob === 'function';
      const canReadJson = typeof (response as Response & { json?: () => Promise<T> }).json === 'function';

      let data: T;
      if (
        requestedResponseType === 'blob' ||
        (
          !requestedResponseType &&
          canReadBlob &&
          !canReadJson &&
          !contentType.includes('application/json') &&
          !contentType.includes('+json')
        )
      ) {
        data = await response.blob() as T;
      } else if (requestedResponseType === 'text') {
        data = await response.text() as T;
      } else {
        data = await response.json();
      }
      
      // Log successful request
      logger.debug('API request successful', {
        endpoint,
        requestLabel,
        method,
        duration,
        status: response.status,
      });
      
      return data;
    } catch (error) {
      const duration = Date.now() - startTime;
      recordSlowApi(endpoint, method, duration);

      if (error instanceof ApiClientError) {
        logger.error('API request failed', {
          endpoint,
          requestLabel,
          method,
          duration,
          error: error.message,
          status: error.status,
          code: error.code,
          kind: error.kind,
        });
        throw error;
      }
      
      // Handle timeout errors
      if (requestSignal.didTimeout() || (error instanceof DOMException && error.name === 'TimeoutError')) {
        logger.error('API request timeout', { endpoint, timeout, duration });
        throw new ApiClientError(
          `Request timeout after ${timeout}ms`,
          408,
          'REQUEST_TIMEOUT',
          undefined,
          'timeout',
          true
        );
      }
      
      // Handle abort errors as expected cancellation so query libraries can
      // treat them as cancelled work instead of terminal failures.
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.info('[api-client] request aborted', {
          endpoint,
          requestLabel,
          method,
          duration,
          aborted: requestSignal.signal.aborted,
        });
        logger.debug('API request aborted', { endpoint, duration });
        throw error;
      }
      
      // Network or other errors
      logger.error('Network error', { endpoint, duration, error });
      throw new ApiClientError(
        'Network error or server unavailable',
        0,
        'NETWORK_ERROR',
        undefined,
        'network',
        true
      );
    } finally {
      requestSignal.cleanup();
    }
  }

  private async handleErrorResponse<T>(
    response: Response,
    endpoint: string,
    options: RequestConfig,
    retryCount: number
  ): Promise<T> {
    let errorData: ApiError;
    
    try {
      errorData = await response.json();
    } catch {
      // If we can't parse the error response, create a generic one
      errorData = {
        error: {
          code: 'UNKNOWN_ERROR',
          message: `HTTP ${response.status}: ${response.statusText}`,
        },
      };
    }

    const { error } = errorData;

    if (
      error.message?.toLowerCase().includes('doctor control session') ||
      error.message?.toLowerCase().includes('doctor control request')
    ) {
      clearDoctorControlStorage();
    }
    
    const maxRetries =
      typeof options.retries === 'number'
        ? Math.max(0, options.retries)
        : 3;

    // Handle rate limit errors with exponential backoff
    if (response.status === 429 && retryCount < maxRetries) {
      // Calculate exponential backoff: 1s, 2s, 4s
      const backoffMs = Math.pow(2, retryCount) * 1000;
      const retryAfter = response.headers.get('Retry-After');
      const waitTime = parseRetryAfterMs(retryAfter, backoffMs);
      
      logger.warn('Rate limit exceeded, retrying with backoff', {
        endpoint,
        retryCount: retryCount + 1,
        waitTimeMs: waitTime,
      });
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, waitTime));
      
      // Retry the request
      return await this.executeRequest<T>(endpoint, options, retryCount + 1);
    }
    
    // Handle authentication errors by attempting token refresh and retry
    if (response.status === 401 && retryCount < 1) {
      try {
        logger.info('401 error, attempting token refresh and retry', { endpoint });
        
        const { data, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !data.session) {
          if (refreshError && this.isInvalidRefreshTokenError(refreshError)) {
            await this.clearInvalidSession('retry_after_401_failed', refreshError);
          } else {
            logger.error('Token refresh failed', refreshError, { stage: 'handleErrorResponse', endpoint });
          }
          throw new ApiClientError(
            'Authentication required. Please sign in again.',
            401,
            'AUTHENTICATION_REQUIRED',
            undefined,
            'auth',
            false
          );
        }
        
        logger.debug('Token refreshed, retrying request', { endpoint });
        
        // Retry the request with the new token
        return await this.executeRequest<T>(endpoint, options, retryCount + 1);
        
      } catch (error) {
        if (error instanceof ApiClientError) {
          throw error;
        }
        
        throw new ApiClientError(
          'Authentication required. Please sign in again.',
          401,
          'AUTHENTICATION_REQUIRED',
          undefined,
          'auth',
          false
        );
      }
    }

    const kind = classifyApiErrorFromStatus(response.status, error.code);
    throw new ApiClientError(
      error.message || 'An error occurred',
      response.status,
      error.code,
      error.details,
      kind,
      isRetryableErrorKind(kind)
    );
  }

  // HTTP Methods
  async get<T>(
    endpoint: string, 
    params?: Record<string, any>, 
    config?: Omit<RequestConfig, 'method' | 'body'>
  ): Promise<T> {
    let finalEndpoint = endpoint;
    
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        finalEndpoint += (endpoint.includes('?') ? '&' : '?') + queryString;
      }
    }

    return this.makeRequest<T>(finalEndpoint, {
      method: 'GET',
      ...config,
    });
  }

  async post<T>(
    endpoint: string, 
    data?: any, 
    config?: Omit<RequestConfig, 'method'>
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...config,
    });
  }

  async patch<T>(
    endpoint: string, 
    data?: any, 
    config?: Omit<RequestConfig, 'method'>
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...config,
    });
  }

  async put<T>(
    endpoint: string, 
    data?: any, 
    config?: Omit<RequestConfig, 'method'>
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...config,
    });
  }

  async delete<T>(
    endpoint: string, 
    config?: Omit<RequestConfig, 'method' | 'body'>
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'DELETE',
      ...config,
    });
  }

  // Batch request method for multiple API calls
  async batch(requests: Array<{
    endpoint: string;
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    data?: any;
    params?: Record<string, any>;
    config?: RequestConfig;
  }>): Promise<any[]> {
    const promises = requests.map(({ endpoint, method = 'GET', data, params, config }) => {
      switch (method) {
        case 'GET':
          return this.get(endpoint, params, config);
        case 'POST':
          return this.post(endpoint, data, config);
        case 'PUT':
          return this.put(endpoint, data, config);
        case 'PATCH':
          return this.patch(endpoint, data, config);
        case 'DELETE':
          return this.delete(endpoint, config);
        default:
          throw new Error(`Unsupported method: ${method}`);
      }
    });

    return Promise.all(promises);
  }

  // Optimistic update helper
  async optimisticUpdate<T>(
    endpoint: string,
    data: any,
    rollbackFn?: () => void
  ): Promise<T> {
    try {
      const result = await this.put<T>(endpoint, data);
      return result;
    } catch (error) {
      // Rollback on error
      if (rollbackFn) {
        rollbackFn();
      }
      throw error;
    }
  }

  // File upload helper (for presigned URLs)
  async uploadFile(presignedUrl: string, file: File | Blob, contentType: string): Promise<void> {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    });

    if (!response.ok) {
      throw new ApiClientError(
        'File upload failed',
        response.status,
        'UPLOAD_FAILED'
      );
    }
  }
}

export const apiClient = new ApiClient();
